import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ProjectList from "./ProjectList";
import ActionItemList from "./ActionItemList";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { useFocusManagement } from "./hooks/useFocusManagement";
import { logger } from "../logger";
import {
  useCreateProject,
  useUpdateProject,
  useCreateActionItem,
  useUpdateActionItem,
} from "../hooks/useData";
import type {
  Project as ProjectType,
  ActionItem as ActionItemType,
  EditingProject,
  EditingActionItem,
  FocusState,
} from "./types";

interface ProjectManagerProps {
  projects?: ProjectType[];
  globalActionItems?: ActionItemType[];
}

export default function ProjectManager({
  projects: propProjects,
  globalActionItems: propGlobalActionItems,
}: ProjectManagerProps = {}) {
  // Use props directly instead of local state - React Query will handle updates
  const projects = useMemo(() => propProjects || [], [propProjects]);
  const globalActionItems = useMemo(
    () => propGlobalActionItems || [],
    [propGlobalActionItems],
  );
  const [focusState, setFocusState] = useState<FocusState>({
    mode: "projects",
    projectIndex: -1,
    actionItemIndex: -1,
    expandedProjectId: null,
  });

  const [editingProject, setEditingProject] = useState<EditingProject | null>(
    null,
  );
  const [projectError, setProjectError] = useState<string | null>(null);
  const [editingActionItem, setEditingActionItem] =
    useState<EditingActionItem | null>(null);

  const transitionInProgress = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Mutation hooks
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const createActionItem = useCreateActionItem();
  const updateActionItem = useUpdateActionItem();

  // No need to sync props since we use them directly
  // React Query will handle data updates automatically

  // Project editing functions
  const handleEditProject = (projectIndex: number, fieldIndex: number) => {
    if (projectIndex === -1) {
      // New project
      setEditingProject({
        id: -1,
        name: "",
        desiredOutcome: "",
        fieldIndex,
      });
    } else {
      // Existing project
      const project = projects[projectIndex];
      setEditingProject({
        id: project.id,
        name: project.name,
        desiredOutcome: project.desiredOutcome,
        fieldIndex,
      });
    }
    setFocusState((prev) => ({ ...prev, mode: "editProject" }));
  };

  const handleProjectFieldChange = (
    field: keyof EditingProject,
    value: string,
  ) => {
    setEditingProject((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleProjectSave = useCallback(async () => {
    if (!editingProject) return;

    // Clear any previous errors
    setProjectError(null);

    try {
      if (editingProject.id === -1) {
        // Create new project
        await createProject.mutateAsync({
          name: editingProject.name.trim(),
          desiredOutcome: editingProject.desiredOutcome.trim(),
        });
        setFocusState((prev) => ({
          ...prev,
          mode: "projects",
          projectIndex: 0,
        }));
        setEditingProject(null);
      } else {
        // Update existing project
        await updateProject.mutateAsync({
          id: editingProject.id,
          name: editingProject.name,
          desiredOutcome: editingProject.desiredOutcome,
        });
        setFocusState((prev) => ({ ...prev, mode: "projects" }));
        setEditingProject(null);
      }
    } catch (error) {
      logger.error("Failed to save project", { error });
      // Display error to user and keep form open
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save project";
      if (
        errorMessage.includes("unique") ||
        errorMessage.includes("duplicate")
      ) {
        setProjectError(
          "A project with this name already exists. Please choose a different name.",
        );
      } else {
        setProjectError(errorMessage);
      }
      // Don't clear editingProject or change focus - keep user in the form
    }
  }, [editingProject, createProject, updateProject, setFocusState]);

  const handleProjectCancel = () => {
    setEditingProject(null);
    setProjectError(null);
    setFocusState((prev) => ({ ...prev, mode: "projects" }));
  };

  // Action item editing functions
  const handleEditActionItem = (
    projectId: number,
    actionIndex: number,
    fieldIndex: number,
  ) => {
    if (projectId === -1) {
      // Global action item
      if (actionIndex === -1) {
        // New global action item
        setEditingActionItem({
          id: -1,
          content: "",
          date: "",
          status: "actionable",
          delegatedTo: "",
          fieldIndex,
          projectId: -1,
          actionIndex: -1,
        });
      } else {
        // Existing global action item
        const actionItem = globalActionItems[actionIndex];
        setEditingActionItem({
          id: actionItem.id,
          content: actionItem.content,
          date: actionItem.date || "",
          status: actionItem.status,
          delegatedTo: actionItem.delegatedTo || "",
          fieldIndex,
          projectId: -1,
          actionIndex,
        });
      }
      setFocusState((prev) => ({ ...prev, mode: "editActionItem" }));
    } else {
      // Project action item
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;

      if (actionIndex === -1) {
        // New project action item
        setEditingActionItem({
          id: -1,
          content: "",
          date: "",
          status: "actionable",
          delegatedTo: "",
          fieldIndex,
          projectId,
          actionIndex: -1,
        });
      } else {
        // Existing project action item
        const actionItem = project.actionItems[actionIndex];
        setEditingActionItem({
          id: actionItem.id,
          content: actionItem.content,
          date: actionItem.date || "",
          status: actionItem.status,
          delegatedTo: actionItem.delegatedTo || "",
          fieldIndex,
          projectId,
          actionIndex,
        });
      }
      setFocusState((prev) => ({ ...prev, mode: "editActionItem" }));
    }
  };

  const handleAddNewActionItem = (projectId: number) => {
    handleEditActionItem(projectId, -1, 0);
  };

  const handleActionItemFieldChange = (
    field: keyof EditingActionItem,
    value: string | number,
  ) => {
    setEditingActionItem((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const handleActionItemSave = useCallback(async () => {
    if (!editingActionItem) return;

    try {
      if (editingActionItem.projectId === -1) {
        // Global action item
        if (editingActionItem.actionIndex === -1) {
          // Create new global action item
          await createActionItem.mutateAsync({
            content: editingActionItem.content.trim(),
            status: editingActionItem.status,
            date: editingActionItem.date || undefined,
            delegatedTo: editingActionItem.delegatedTo || undefined,
          });
          setFocusState((prev) => ({
            ...prev,
            mode: "globalActionItems",
            actionItemIndex: globalActionItems.length,
          }));
        } else {
          // Update existing global action item
          const actionItemId =
            globalActionItems[editingActionItem.actionIndex]?.id;
          if (actionItemId) {
            await updateActionItem.mutateAsync({
              id: actionItemId,
              content: editingActionItem.content,
              status: editingActionItem.status,
              date: editingActionItem.date || undefined,
              delegatedTo: editingActionItem.delegatedTo || undefined,
            });
          }
          setFocusState((prev) => ({ ...prev, mode: "globalActionItems" }));
        }
      } else {
        // Project action item
        const project = projects.find(
          (p) => p.id === editingActionItem.projectId,
        );
        if (!project) return;

        if (editingActionItem.actionIndex === -1) {
          // Create new project action item
          await createActionItem.mutateAsync({
            content: editingActionItem.content.trim(),
            projectId: editingActionItem.projectId,
            status: editingActionItem.status,
            date: editingActionItem.date || undefined,
            delegatedTo: editingActionItem.delegatedTo || undefined,
          });
          setFocusState((prev) => ({
            ...prev,
            mode: "actionItems",
            actionItemIndex: project.actionItems.length,
          }));
        } else {
          // Update existing project action item
          const actionItemId =
            project.actionItems[editingActionItem.actionIndex]?.id;
          if (actionItemId) {
            await updateActionItem.mutateAsync({
              id: actionItemId,
              content: editingActionItem.content,
              status: editingActionItem.status,
              date: editingActionItem.date || undefined,
              delegatedTo: editingActionItem.delegatedTo || undefined,
            });
          }
          setFocusState((prev) => ({ ...prev, mode: "actionItems" }));
        }
      }
      setEditingActionItem(null);
    } catch (error) {
      logger.error("Failed to save action item", { error });
      // Optionally show error to user
    }
  }, [
    editingActionItem,
    globalActionItems,
    projects,
    createActionItem,
    updateActionItem,
    setFocusState,
  ]);

  const handleActionItemCancel = () => {
    setEditingActionItem(null);
    if (editingActionItem?.projectId === -1) {
      setFocusState((prev) => ({ ...prev, mode: "globalActionItems" }));
    } else {
      setFocusState((prev) => ({ ...prev, mode: "actionItems" }));
    }
  };

  const handleActionItemStatusToggle = async (
    projectId: number,
    actionIndex: number,
    newStatus: "actionable" | "done" | "not actionable",
  ) => {
    try {
      if (projectId === -1) {
        // Global action item
        const actionItem = globalActionItems[actionIndex];
        if (actionItem) {
          await updateActionItem.mutateAsync({
            id: actionItem.id,
            content: actionItem.content,
            status: newStatus,
            date: actionItem.date,
            delegatedTo: actionItem.delegatedTo,
          });
        }
      } else {
        // Project action item
        const project = projects.find((p) => p.id === projectId);
        if (project && project.actionItems[actionIndex]) {
          const actionItem = project.actionItems[actionIndex];
          await updateActionItem.mutateAsync({
            id: actionItem.id,
            content: actionItem.content,
            status: newStatus,
            date: actionItem.date,
            delegatedTo: actionItem.delegatedTo,
          });
        }
      }
    } catch (error) {
      logger.error("Failed to toggle action item status", {
        error,
        projectId,
        actionIndex,
        newStatus,
      });
      // Optionally show error to user
    }
  };

  const handleToggleExpansion = (projectId: number) => {
    setFocusState((prev) => ({
      ...prev,
      expandedProjectId:
        prev.expandedProjectId === projectId ? null : projectId,
      mode: prev.expandedProjectId === projectId ? "projects" : "actionItems",
      actionItemIndex: 0,
      projectIndex: projects.findIndex((p) => p.id === projectId),
    }));
  };

  // Keyboard navigation handlers
  const handleEscape = () => {
    if (focusState.mode === "editProject") {
      handleProjectCancel();
    } else if (focusState.mode === "editActionItem") {
      handleActionItemCancel();
    } else if (focusState.mode === "actionItems") {
      setFocusState((prev) => ({ ...prev, mode: "projects" }));
    } else if (focusState.mode === "globalActionItems") {
      setFocusState((prev) => ({
        ...prev,
        mode: "projects",
        projectIndex: projects.length - 1,
      }));
    } else if (
      focusState.mode === "projects" &&
      focusState.expandedProjectId !== null
    ) {
      setFocusState((prev) => ({ ...prev, expandedProjectId: null }));
    }
  };

  const handleEnter = () => {
    if (focusState.mode === "projects") {
      if (focusState.projectIndex === -1) {
        handleEditProject(-1, 0);
      } else {
        const currentProject = projects[focusState.projectIndex];
        if (
          currentProject &&
          focusState.expandedProjectId === currentProject.id
        ) {
          handleEditProject(focusState.projectIndex, 0);
        } else if (currentProject) {
          setFocusState((prev) => ({
            ...prev,
            expandedProjectId: currentProject.id,
          }));
        }
      }
    } else if (focusState.mode === "actionItems") {
      const currentProject = projects[focusState.projectIndex];
      if (currentProject) {
        handleEditActionItem(currentProject.id, focusState.actionItemIndex, 0);
      }
    } else if (focusState.mode === "globalActionItems") {
      handleEditActionItem(-1, focusState.actionItemIndex, 0);
    }
  };

  // Setup hooks
  const { handleKeyDown } = useKeyboardNavigation({
    projects,
    globalActionItems,
    focusState,
    setFocusState,
    onEscape: handleEscape,
    onEnter: handleEnter,
  });

  const { handleFocusChange } = useFocusManagement({
    projects,
    focusState,
    setFocusState,
    transitionInProgress,
  });

  // Click outside handler to save edits
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        if (editingProject) {
          handleProjectSave();
        } else if (editingActionItem) {
          handleActionItemSave();
        }
      }
    },
    [
      editingProject,
      editingActionItem,
      handleProjectSave,
      handleActionItemSave,
    ],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("focusin", handleFocusChange);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("focusin", handleFocusChange);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleKeyDown, handleFocusChange, handleClickOutside]);

  // Auto-focus the "Add New Project" area when component mounts
  useEffect(() => {
    const focusElement = document.querySelector(
      "[data-new-project]",
    ) as HTMLElement;
    if (focusElement) {
      focusElement.focus();
    }
  }, []);

  const currentProject =
    focusState.projectIndex >= 0 ? projects[focusState.projectIndex] : null;

  return (
    <div ref={containerRef} className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
          <strong>Navigation:</strong> ↑↓ or j/k to navigate • Enter to
          expand/edit • Tab to move through fields • Escape to save
        </div>
      </div>

      <ProjectList
        projects={projects}
        onAddNew={() => handleEditProject(-1, 0)}
        onEditProject={handleEditProject}
        onEditActionItem={handleEditActionItem}
        onAddNewActionItem={handleAddNewActionItem}
        onToggleExpansion={handleToggleExpansion}
        onActionItemStatusToggle={handleActionItemStatusToggle}
        onProjectFieldChange={handleProjectFieldChange}
        onProjectSave={handleProjectSave}
        onProjectCancel={handleProjectCancel}
        onActionItemFieldChange={handleActionItemFieldChange}
        onActionItemSave={handleActionItemSave}
        onActionItemCancel={handleActionItemCancel}
        focusState={{
          projectIndex: focusState.projectIndex,
          actionItemIndex: focusState.actionItemIndex,
          expandedProjectId: focusState.expandedProjectId,
          isAddingNewProject:
            focusState.projectIndex === -1 && focusState.mode === "projects",
        }}
        editingProject={editingProject || undefined}
        projectError={projectError}
        editingActionItem={editingActionItem || undefined}
      />

      {/* Global Action Items Section */}
      <div className="mt-8">
        <h2 className="text-2xl font-semibold mb-4">Global Action Items</h2>
        <ActionItemList
          items={globalActionItems}
          projectId={-1}
          onAddNew={handleAddNewActionItem}
          onEdit={handleEditActionItem}
          onStatusToggle={handleActionItemStatusToggle}
          onFieldChange={handleActionItemFieldChange}
          onSave={handleActionItemSave}
          onCancel={handleActionItemCancel}
          focusState={{
            itemIndex: focusState.actionItemIndex,
            isAddingNew:
              focusState.actionItemIndex === -1 &&
              focusState.mode === "globalActionItems",
          }}
          editingItem={editingActionItem || undefined}
        />
      </div>

      {/* Status Bar */}
      <div className="mt-8 p-3 bg-gray-100 rounded text-sm text-gray-600">
        <strong>Current State:</strong> {focusState.mode} |
        <strong> Project:</strong>{" "}
        {focusState.projectIndex === -1
          ? "New"
          : `${focusState.projectIndex + 1}/${projects.length}`}{" "}
        |
        {focusState.mode === "actionItems" && (
          <>
            <strong> Action:</strong>{" "}
            {focusState.actionItemIndex === -1
              ? "New"
              : `${focusState.actionItemIndex + 1}/${
                  currentProject?.actionItems.length || 0
                }`}
          </>
        )}
        {focusState.mode === "globalActionItems" && (
          <>
            <strong> Global Action:</strong>{" "}
            {focusState.actionItemIndex === -1
              ? "New"
              : `${focusState.actionItemIndex + 1}/${globalActionItems.length}`}
          </>
        )}
      </div>
    </div>
  );
}
